import { useCallback, useContext, useEffect, useState } from 'react';

import {
  control,
  ControlPosition,
  data,
  HtmlMarker,
  layer,
  Popup,
  source
} from 'azure-maps-control';
import { AzureMapsContext } from 'react-azure-maps';
import { useRecoilValue } from 'recoil';

import type { FC } from 'react';
import type { IAzureMapsContextProps } from 'react-azure-maps';
import type {
  Event,
  LatitudeLongitudePair,
  PlanningEntityMap,
  Schedule
} from '../types';

import {
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  DARK,
  GRAY,
  GRAY_SECONDARY,
  MINT,
  ORANGE,
  ORANGE_SECONDARY,
  PRIMARY,
  RED,
  SECONDARY,
  TURQUOISE,
  YELLOW
} from 'constants/colors';

import { selectedDayPartsAtom } from '../atoms';

import { Box, styled, Typography } from '@mui/material';

import MapComponent from 'components/shared/Map/MapComponent';
import cleanMap from 'components/shared/Map/cleanMap';
import useSelectedDate from '../hooks/useSelectedDate';
import { EventTooltip } from '../components';
import useTooltipRoots from '../hooks/useTooltipRoots';
import dayjs from 'dayjs';
import { Icon } from '@sdb/planning-ui';

const StyledNoRoutesPlaceholder = styled(Box)`
  z-index: 110;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  gap: 8px;
`;

function NoRoutesPlaceholder() {
  return (
    <StyledNoRoutesPlaceholder>
      <Icon type="location-dot-slash" sx={{ fontSize: '32px' }} />
      <Typography>Geen routes om te tonen.</Typography>
    </StyledNoRoutesPlaceholder>
  );
}

const StyledMapComponent = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  '.pin, .pin-to-plan': {
    width: '30px',
    height: '30px',
    borderRadius: '50% 50% 50% 0',
    transform: 'rotate(-45deg)',
    left: '50px',
    top: '50px',
    margin: '-20px 0 0 -20px',
    backgroundSize: 'cover',
    overflow: 'hidden'
  },
  '.pin': {
    border: '3px solid ' + PRIMARY,
    backgroundColor: PRIMARY
  },
  '.pin-to-plan': {
    border: '3px solid ' + ORANGE,
    backgroundColor: ORANGE
  },
  '.pin img, .pin-to-plan img, .svg': {
    transform: 'rotate(45deg)',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '.pin:hover': {
    cursor: 'pointer'
  },

  '.number': {
    position: 'absolute',
    top: '45%',
    left: '0%',
    transform: 'rotate(45deg)',
    fontSize: '10px',
    color: 'rgb(0, 0, 0)',
    fontFamily: 'SourceSansPro-Bold, sans-serif',
    textShadow:
      '-0.5px 0.5px 0 white, 0.5px 0.5px 0.5px white, 0.5px -0.5px 0 white, -0.5px -0.5px 0 white'
  },

  '.popup': {
    position: 'relative',
    padding: '5px',
    borderRadius: '10px',
    border: '2px solid transparent',
    lineHeight: '1.4'
  },
  '.title': {
    fontSize: '20px',
    marginBottom: '10px',
    fontFamily: 'SourceSansPro-Bold, sans-serif',
    display: 'flex',
    flexDirection: 'column'
  },
  '.address': {
    fontSize: '12px',
    fontFamily: 'SourceSansPro-Regular, sans-serif',
    color: '#8c8b89'
  },
  '.content-table': {
    borderCollapse: 'collapse',
    fontSize: '1.0em',
    // minWidth: '400px',
    borderRadius: '5px 5px 0 0',
    overflow: 'hidden',
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.15)',
    fontFamily: 'Trebuchet MS, sans-serif'
  },
  '.content-table thead tr': {
    backgroundColor: PRIMARY,
    color: '#ffffff',
    textAlign: 'left',
    fontFamily: 'SourceSansPro-Bold, sans-serif'
  },
  '.td:first-child': {
    fontFamily: 'SourceSansPro-Bold, sans-serif'
  },
  '.content-table th': {
    padding: '12px 15px'
  },
  '.content-table td': {
    padding: '4px 11px'
  },
  '.content-table tbody tr': {
    borderBottom: '1px solid #dddddd'
  },
  // '.content-table tbody tr:nth-of-type(even)': {
  //   backgroundColor: '#f3f3f3'
  // },
  // '.content-table tbody tr:last-of-type': {
  //   borderBottom: '2px solid ' + PRIMARY
  // },
  '.content-table tbody tr.active-row': {
    fontFamily: 'SourceSansPro-Bold, sans-serif',
    color: PRIMARY
  },
  '.col-1': {
    fontFamily: 'SourceSansPro-Bold, sans-serif'
  },
  '.col-let': {
    fontFamily: 'SourceSansPro-Bold, sans-serif',
    color: '#ff6f00'
  }
}));

const routeColors = [
  PRIMARY,
  SECONDARY,
  ORANGE,
  BLUE,
  ORANGE_SECONDARY,
  BLUE_DARK,
  TURQUOISE,
  MINT,
  BLUE_LIGHT,
  DARK,
  YELLOW,
  RED,
  GRAY,
  GRAY_SECONDARY
];

interface Props {
  planningEntities: PlanningEntityMap | null;
  // Used to filter the appointments
  scheduleId?: string;
  start?: string;
  // ^
}

const getHasClientLocation = (event: Event) => {
  const { category, location } = event;
  return (
    category == 'CLIENT' &&
    location?.locationType === 'CLIENTADDRESS' &&
    (location?.address || (location?.latitude && location?.longitude))
  );
};

const MapController: FC<Props> = ({ planningEntities, scheduleId, start }) => {
  'use memo';

  const selectedDayParts = useRecoilValue(selectedDayPartsAtom);
  const [selectedDate, setSelectedDate] = useSelectedDate();

  const { mapRef, isMapReady } =
    useContext<IAzureMapsContextProps>(AzureMapsContext);

  const [datasource, setDatasource] = useState<source.DataSource>();
  const { renderTooltip, unmountTooltips } = useTooltipRoots();

  const addMarkers = useCallback(
    function addMarkers(
      events: Event[],
      positions: Array<object | number[]>,
      toBePlanned: boolean
    ) {
      // filter out the routes
      events
        .filter(
          (e) =>
            e.category == 'CLIENT' &&
            e.location.locationType == 'CLIENTADDRESS' &&
            e.location.longitude &&
            e.location.latitude
        )
        .forEach((event: Event) => {
          const { clients, location } = event;

          const client = clients[0];

          const position: number[] = [
            location.longitude as number,
            location.latitude as number
          ];

          positions.push(position);

          const firstLetter = client?.lastName?.charAt(0)?.toUpperCase();

          // if the client has a base64 image, img will be the base64 image, otherwise img will be null
          const img = client?.base64Image
            ? `data:image/jpeg;base64,${client?.base64Image}`
            : null;

          // if toBePlanned is true, the svg will be orange, otherwise it will be blue
          const pin = `
          <svg class="svg" height="24" width="24">
            <rect fill="${
              toBePlanned ? ORANGE : PRIMARY
            } height="24" width="24"></rect>

            <text fill="#fff" font-size="18" font-weight="50" x="50%" y="50%" text-anchor="middle" alignment-baseline="middle">
              ${firstLetter}
            </text>
          </svg>
          `;

          const htmlContent = `
          <div>
            <div class=${toBePlanned ? 'pin-to-plan' : 'pin'}><div>
            ${img ? `<img src= ${img} />` : pin}
          </div>

          <div class="number"></div>
        `;

          const tooltipBody = document.createElement('div');

          renderTooltip(
            tooltipBody,
            <EventTooltip event={event} isStaticHTMLRender />
          );

          const marker = new HtmlMarker({
            position,
            htmlContent: htmlContent,

            popup: new Popup({
              content: tooltipBody,
              pixelOffset: [0, -30]
            })
          });

          mapRef?.events.add('click', marker, () => {
            marker.togglePopup();
          });

          mapRef?.markers.add(marker);
        });
    },
    [mapRef, renderTooltip]
  );

  const filterBySchedule = useCallback(
    (schedule: Schedule) => {
      if (scheduleId) return schedule.planningEntityId === scheduleId;
      return true;
    },
    [scheduleId]
  );

  const updateMap = useCallback(
    (datasource: source.DataSource) => {
      datasource.clear();
      unmountTooltips();
      mapRef?.markers.clear();
      if (planningEntities) {
        const features = Object.values(planningEntities)
          .filter((s) => s.id)
          .map((planningEntities) => planningEntities.schedules)
          .flat()
          .filter(filterBySchedule)
          .map((schedule, index) => {
            const points =
              selectedDayParts.length < 1 ||
              selectedDayParts.includes(schedule.dayPart ?? 'MORNING')
                ? schedule.events
                    .filter((o) => o.location?.points)
                    .map((o) => {
                      const pairs: LatitudeLongitudePair[] = JSON.parse(
                        o.location.points ?? ''
                      );
                      return pairs.map((p) => [p.longitude, p.latitude]);
                    })
                    .flatMap((p) => p)
                : [];
            const lineString = new data.LineString(points.length ? points : []);
            return new data.Feature(lineString, {
              color: routeColors[index]
            });
          })
          .filter((item): item is data.Feature<data.LineString, any> => !!item);

        datasource.add(features);
        const positions: Array<object | number[]> = [];
        Object.values(planningEntities)
          .map((planningEntity) => planningEntity.schedules)
          .flat()
          .filter(filterBySchedule)
          .forEach((schedule) => {
            if (
              selectedDayParts.length < 1 ||
              selectedDayParts.includes(schedule.dayPart ?? 'MORNING')
            ) {
              addMarkers(schedule.events, positions, false);
            }
          });

        !scheduleId &&
          Object.values(planningEntities)
            .filter(({ type }) => type === 'ORGANIZATIONAL_UNIT_TO_PLAN')
            .map(({ events }) => {
              if (events) {
                addMarkers(events, positions, true); // toBePlanned = true
              }
            });

        const positionsBoundingBox = data.BoundingBox.fromLatLngs(positions);
        const featuresBoundingBox = data.BoundingBox.fromData(features);

        mapRef?.setCamera({
          bounds: data.BoundingBox.merge(
            featuresBoundingBox,
            positionsBoundingBox
          ),
          padding: 50
        });
      }
    },
    [
      addMarkers,
      filterBySchedule,
      mapRef,
      planningEntities,
      scheduleId,
      selectedDayParts,
      unmountTooltips
    ]
  );

  useEffect(() => {
    // If 'start' was passed (when forwarded from a week view) we use that as the currently selected date
    if (start && dayjs(start).isValid()) setSelectedDate(dayjs(start).toDate());
  }, [start, setSelectedDate]);

  // Set the map up once, as soon as it is ready. This must not depend on
  // updateMap: that changes with every new planningEntities, which would tear
  // the zoom control, data source and line layer down and build them back up on
  // every planning update. Drawing is left to the effect below.
  useEffect(() => {
    if (!isMapReady || !mapRef) {
      return;
    }

    const zoomControl = new control.ZoomControl();

    /* Add the zoom control to the map*/
    mapRef.controls.add(zoomControl, {
      position: ControlPosition.BottomRight
    });

    const newDatasource = new source.DataSource();
    mapRef.sources.add(newDatasource);

    const lineLayerRef = new layer.LineLayer(newDatasource, 'line', {
      strokeColor: ['get', 'color'],
      strokeWidth: 5,
      lineJoin: 'round',
      lineCap: 'round'
    });
    mapRef.layers.add(lineLayerRef);

    setDatasource(newDatasource);

    // Take everything off again when the map instance is replaced, see cleanMap.
    return () =>
      cleanMap(mapRef, {
        lineLayer: lineLayerRef,
        datasource: newDatasource,
        zoomControl
      });
  }, [isMapReady, mapRef]);

  useEffect(() => {
    if (isMapReady && mapRef && datasource) {
      updateMap(datasource);
    }
  }, [datasource, isMapReady, mapRef, updateMap]);

  useEffect(() => {
    mapRef?.popups.clear();
  }, [selectedDate, mapRef]);

  const hasConnectedRoutes =
    planningEntities &&
    Object.values(planningEntities)
      .map(({ schedules }) => schedules)
      .flat()
      .filter(filterBySchedule)
      .some(
        ({ events }) =>
          events.length > 1 &&
          events.some((event) => getHasClientLocation(event))
      );

  return (
    <StyledMapComponent>
      {!hasConnectedRoutes && <NoRoutesPlaceholder />}
      <MapComponent />
    </StyledMapComponent>
  );
};

export default MapController;
